import _ from "lodash";
import { useMemo } from "react";
import useReduxState from "Hooks/useReduxState";
import { getOrganizationCurriculumPrograms } from "modules/CommonSelectors";
import {
  getSelectedSchoolDetailsForCalendar,
  getEnabledProgramIds,
} from "MyCalendar/modules/Utils";
import { getIsCombineCurriculumHomePageEnabled } from "Platform/modules/PlatformUtils";

/**
 * Custom hook to fetch and filter schools from organization curriculum programs.
 * Schools are curriculum programs with programType === "SCHOOL".
 *
 * Filters based on enabled curriculum programs following SnP module pattern:
 * - In combined curriculum mode: filters by user's accessible curriculum programs
 * - In single curriculum mode: filters by selected curriculum program
 *
 * @returns {Object} Object containing schools array
 * @returns {Array} schools - Array of school curriculum programs
 *
 * @example
 * const { schools } = useSchools();
 */
const useSchools = () => {
  const curriculumPrograms = useReduxState(getOrganizationCurriculumPrograms);
  const { currentCurriculumProgramId: selectedCurriculumId } = useReduxState(
    getSelectedSchoolDetailsForCalendar
  );
  const schools = useMemo(() => {
    // Get the curriculum program IDs that user has access to
    const isCombinedCurriculum = getIsCombineCurriculumHomePageEnabled();

    let enabledCurriculumProgramIds;
    if (isCombinedCurriculum) {
      // In combined curriculum mode, get enabled program IDs from user's
      // accessible curriculum programs
      enabledCurriculumProgramIds = getEnabledProgramIds();
    } else {
      // In single curriculum mode, use the selected curriculum
      enabledCurriculumProgramIds = [selectedCurriculumId];
    }

    // Filter schools by enabled curriculum program IDs and SCHOOL type
    return _.filter(curriculumPrograms, program => {
      return (
        program.programType === "SCHOOL" &&
        _.includes(enabledCurriculumProgramIds, program.id)
      );
    });
  }, [curriculumPrograms, selectedCurriculumId]);

  return { schools };
};

export default useSchools;
